import dayjs from "dayjs";
import {
    Calendar,
    ChevronDown,
    DoorClosed,
    Loader2, Search,
    Settings,
    User,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import AppSidebar from "~/components/layout/AppSidebar";
import OutstandingDocumentationBell from "~/components/layout/OutstandingDocumentationBell";
import { Button } from "~/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "~/components/ui/command";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Separator } from "~/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "~/components/ui/sidebar";
import LocalDateTime from "~/lib/localdatetime";
import { CreateOrModifyAppointmentDrawer } from '~/pages/AppointmentsCalendar';
import { useAppointmentsCalendarStore } from "~/stores/appointments-calendar-store";
import { useCreateOrModifyAppointmentStore } from "~/stores/create-appointment-store";
import { useUserStore } from "~/stores/user-store";
import { getAppointmentStatusLabel } from '~/types/server-types/AppointmentStatus';
import useAuth from "../../auth/useAuth";
import { Badge } from '../ui/badge';

const CurrentTimeDisplay = () => {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        // Update the time immediately
        setCurrentTime(new Date());

        // Set up an interval to update the time every 5 seconds
        const intervalId = setInterval(() => {
            setCurrentTime(new Date());
        }, 5000);

        // Clean up the interval when the component unmounts
        return () => clearInterval(intervalId);
    }, []); // Empty dependency array means this runs once on mount

    const displayTimezone = timezone || 'Europe/Berlin';

    return (
        <div className='flex flex-row items-center gap-1'>
            <span className='leading-normal text-sm'>
                {dayjs.tz(currentTime, displayTimezone).format('D. MMMM YYYY, HH:mm')} Uhr ({displayTimezone})
            </span>
        </div>
    );
};

const CustomerOrAppointmentSearchBar = () => {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );

    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const trpcUtils = trpc.useUtils();

    const customersQuery = trpc.customers.searchAndGetAppointments.useQuery({
        query: searchQuery,
    }, {
        enabled: searchQuery.length > 0,
    });

    const customers = useMemo(() => {
        if (customersQuery.data?.customers) {
            return customersQuery.data.customers.map((customer) => ({
                id: customer.id,
                name: `${customer.firstName} ${customer.lastName}`,
                email: customer.email,
                phone: customer.phone,
            }))
        }
        return [];
    }, [customersQuery]);

    // Dummy data for appointments
    const appointments = useMemo(() => {
        if (customersQuery.data?.appointments) {
            return customersQuery.data.appointments.map((appointment) => ({
                id: appointment.id,
                title: appointment.displayName,
                customer: appointment.customer,
                status: appointment.status,
                startTime: LocalDateTime.deserialize(appointment.startLocalDatetimeSerialized),
                endTime: LocalDateTime.deserialize(appointment.endLocalDatetimeSerialized),
                practitioners: appointment.practitioners,
            })).sort((a, b) => b.startTime.toUTC().getTime() - a.startTime.toUTC().getTime())
        }
        return [];
    }, [customersQuery]);

    const [showMoreAppointments, setShowMoreAppointments] = useState<boolean>(false);
    const [showMoreCustomers, setShowMoreCustomers] = useState<boolean>(false);

    // Open popover only when typing (not on focus)
    useEffect(() => {
        setShowMoreAppointments(false);
        if (searchQuery.length > 0 && !open) {
            setOpen(true);
        } else if (searchQuery.length === 0 && open) {
            setOpen(false);
        }
    }, [searchQuery]);

    const handleSelectCustomer = (id: number) => {
        window.open(`/customer/${id}`, '_blank');
    }

    const handleSelectAppointment = (appointmentId: string) => {
        console.log("Selected appointment:", appointmentId);
        setOpen(false);
        setSearchQuery('');

        useCreateOrModifyAppointmentStore.setState({
            appointmentId: appointmentId,
            mode: 'modify',
        });

        useAppointmentsCalendarStore.setState({
            createOrModifyAppointmentDrawerOpen: true,
        });
    };

    return (
        <>
            <CreateOrModifyAppointmentDrawer
                onSubmit={() => {
                    trpcUtils.appointments.getAppointmentsForBoard.invalidate();
                    trpcUtils.customers.getOne.invalidate();
                }}
            />
            <div className="relative w-full max-w-xs">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <div className='w-full flex items-center bg-white/10 border border-gray-200 rounded-md px-2 py-1 transition-all duration-200'>
                            <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <Input
                                ref={inputRef}
                                type="text"
                                placeholder="Suchen nach Terminen und Kunden..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full min-w-[250px] shadow-none border-0 p-0 focus:!ring-0 focus:!outline-none bg-transparent text-sm"
                            // Removed the onFocus handler to prevent opening on focus
                            />
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        autoFocus={false}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        className="p-0 w-screen max-w-lg"
                        align="start"
                        sideOffset={4}
                        onEscapeKeyDown={() => setOpen(false)}
                        onInteractOutside={() => setOpen(false)}
                    >
                        <Command className="rounded-lg border border-gray-200 shadow-md overflow-hidden">
                            <CommandList className='max-h-[700px]'>
                                <CommandEmpty>{customersQuery.isLoading ? (
                                    <div className='w-full flex flex-row items-center justify-center p-4'>
                                        <Loader2 className='animate-spin' />
                                    </div>
                                ) : 'Keine Ergebnisse gefunden.'}</CommandEmpty>
                                {appointments.length > 0 && (
                                    <CommandGroup heading="Termine">
                                        {(showMoreAppointments ? appointments : appointments.slice(0, 4)).map(appointment => (
                                            <CommandItem
                                                key={appointment.id}
                                                value={appointment.id}
                                                onSelect={() => handleSelectAppointment(appointment.id)}
                                                className="flex items-start py-2 px-2 cursor-pointer"
                                            >
                                                <div className="flex-shrink-0 mr-3 mt-1">
                                                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                        <Calendar className="h-4 w-4 text-purple-600" />
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-sm">{`${appointment.customer.firstName} ${appointment.customer.lastName}`} - {appointment.title}</span>
                                                    <Badge variant='outline'>
                                                        {getAppointmentStatusLabel(appointment.status)}
                                                    </Badge>
                                                    <span className="text-xs text-gray-500">{appointment.startTime.format("dd.MM.yyyy")}</span>
                                                    <span className="text-xs text-gray-500">{appointment.startTime.format("HH:mm")} - {appointment.endTime.format("HH:mm")} Uhr</span>
                                                    {appointment.practitioners.length > 0 && (
                                                        <span className="text-xs text-gray-500">Behandler: {appointment.practitioners.join(", ")}</span>
                                                    )}
                                                </div>
                                            </CommandItem>
                                        ))}
                                        {!showMoreAppointments && appointments.length > 5 && (
                                            <Button
                                                variant='link'
                                                onClick={() => {
                                                    setShowMoreAppointments(true);
                                                }}
                                                className='my-2'
                                            >
                                                Mehr Ergebnisse
                                            </Button>
                                        )}
                                        {showMoreAppointments && appointments.length > 5 && (
                                            <Button
                                                variant='link'
                                                onClick={() => {
                                                    setShowMoreAppointments(false);
                                                }}
                                                className='my-2'
                                            >
                                                Weniger Ergebnisse
                                            </Button>
                                        )}
                                    </CommandGroup>
                                )}
                                {customers.length > 0 && (
                                    <CommandGroup heading="Kunden">
                                        {(showMoreCustomers ? customers : customers.slice(0, 4)).map(customer => (
                                            <CommandItem
                                                key={customer.id}
                                                value={customer.id.toString()}
                                                onSelect={() => handleSelectCustomer(customer.id)}
                                                className="flex items-start py-2 px-2 cursor-pointer"
                                            >
                                                <div className="flex-shrink-0 mr-3 mt-1">
                                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <User className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-sm">{customer.name}</span>
                                                    <span className="text-xs text-gray-500">{customer.email}</span>
                                                    <span className="text-xs text-gray-500">{customer.phone}</span>
                                                </div>
                                            </CommandItem>
                                        ))}

                                        {!showMoreCustomers && customers.length > 5 && (
                                            <Button
                                                variant='link'
                                                onClick={() => {
                                                    setShowMoreCustomers(true);
                                                }}
                                                className='my-2'
                                            >
                                                Mehr Ergebnisse
                                            </Button>
                                        )}
                                        {showMoreCustomers && customers.length > 5 && (
                                            <Button
                                                variant='link'
                                                onClick={() => {
                                                    setShowMoreCustomers(false);
                                                }}
                                                className='my-2'
                                            >
                                                Weniger Ergebnisse
                                            </Button>
                                        )}
                                    </CommandGroup>
                                )
                                }
                            </CommandList >
                        </Command >
                    </PopoverContent >
                </Popover >
            </div >
        </>
    );
};

const Layout = () => {
    const navigate = useNavigate();

    const { logout, roles } = useAuth();

    const { username, role } = useUserStore(
        useShallow((state) => ({
            username: `${state.user?.firstName} ${state.user?.lastName}`,
            role: state.user?.role
        }))
    );

    return (
        <>
            <SidebarProvider>
                <AppSidebar />
                <SidebarInset>
                    <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4 bg-white">
                        <div className='w-full flex flex-row gap-2 flex-[1_1_25%]'>
                            <div className='w-full flex flex-row items-center gap-4'>
                                <SidebarTrigger />
                                <Separator orientation="vertical" className="mr-2 h-4" />
                                <CustomerOrAppointmentSearchBar />
                            </div>
                        </div>

                        <div className='w-full flex flex-row justify-center gap-2 flex-[1_1_50%]'>
                            <CurrentTimeDisplay />
                        </div>

                        <div className='w-full flex flex-row gap-2 flex-[1_1_25%]'>
                            <div className='w-full flex flex-row items-center justify-end gap-2'>
                                <OutstandingDocumentationBell />
                                <DropdownMenu>
                                    <DropdownMenuTrigger>
                                        <div className='flex flex-row items-center gap-2'>
                                            <span className='leading-normal'>{username}</span>
                                            <ChevronDown className='w-4 h-4' />
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => navigate("/userSettings")}>
                                            <Settings className='w-4 h-4' />
                                            Einstellungen
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={logout}>
                                            <DoorClosed className='w-4 h-4' />
                                            Abmelden
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </header>

                    <Outlet />
                </SidebarInset>
            </SidebarProvider>
        </>
    );
};

export default Layout;
